import { FileText, Folder, X } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * A single recent entry, normalized into the minimal shape the list needs.
 * Mapped from `RecentFile` / `RecentWorkspace` by the caller.
 */
export interface RecentItemVM {
  /** Stable handle id — used both as the React key and the remove key. */
  id: string;
  /** Display name (basename). */
  name: string;
  /** Absolute path, or '' when the handle has none (e.g. web). */
  path: string;
}

export interface RecentsListProps {
  /** The full list of recent items (already most-recent-first). */
  items: RecentItemVM[];
  /** Leading glyph: a file or a folder. */
  icon: 'file' | 'folder';
  /** Message shown when there are no items. */
  emptyMessage: string;
  /** Open the chosen entry (click / Enter on the row). */
  onOpen: (item: RecentItemVM) => void;
  /** Remove the entry from recents, keyed by id. */
  onRemove: (id: string) => void;
  /**
   * Max entries to display. The store keeps up to 100; the welcome screen only
   * ever shows the most recent few. Defaults to 10.
   */
  max?: number;
}

const DEFAULT_MAX = 10;

/**
 * The recent-files / recent-workspaces list on the welcome screen. Renders at
 * most `max` entries (the persisted store may hold far more) and reveals a
 * small remove (X) button on hover / focus of each row. The remove button is a
 * sibling of the open button (never nested), so clicking it never triggers an
 * open; it still stops propagation defensively.
 */
export function RecentsList({
  items,
  icon,
  emptyMessage,
  onOpen,
  onRemove,
  max = DEFAULT_MAX,
}: RecentsListProps): ReactElement {
  if (items.length === 0) {
    return <p className="mp-welcome__empty">{emptyMessage}</p>;
  }

  const shown = items.slice(0, max);
  const Glyph = icon === 'folder' ? Folder : FileText;

  return (
    <ul className="mp-welcome__list" role="list">
      {shown.map((item) => (
        <li key={item.id} className="mp-welcome__item">
          <button
            type="button"
            className="mp-welcome__item-btn"
            onClick={() => onOpen(item)}
            title={item.path || item.name}
          >
            <Glyph size={14} aria-hidden className="mp-welcome__item-icon" />
            <span className="mp-welcome__item-name">{item.name}</span>
            {item.path && <span className="mp-welcome__item-path">{item.path}</span>}
          </button>
          <button
            type="button"
            className="mp-welcome__item-x"
            aria-label="Remove from recents"
            title="Remove from recents"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item.id);
            }}
          >
            <X size={13} aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}
